import React, { useEffect, useState, useRef } from 'react'
import CreateUpdateSiteForm from './form/CreateUpdateSiteForm'
import { LoadingBox } from './components/LoadingBox'
import { useDispatch, useSelector } from 'react-redux'
import {
  getErrorMessage,
  getFullDomainValue,
  getTLDs,
  pluralize,
  onUpgradePlan,
  canSubmitUpload,
  isWasmerFile,
  isTrialAccount,
  hasFeature
} from 'utils/general'
import {
  fetchUserData,
  setAction,
  setDraggedFile,
  setPreviewData,
  setPreviewModal,
  setSiteType,
  showCreateSiteModal,
  toggleModal,
  saveFormValues,
  isPresignedUploadInProgress
} from 'Manage/actions'
import { createNewSite, getSite, updateSite } from './actions'
import { createNewSiteRequest, getUpdatedFields, updateSiteRequest, validate, checkAccountSizeLimit } from './validate'
import {
  ACTION_CREATE,
  ACTION_UPDATE,
  STATE_ERROR,
  STATE_LOADING,
  STATE_READY,
  STATE_SUCCESS,
  STATE_REQUESTED,
  STATE_PREVIEW,
  STATE_PDF_CONVERTING
} from './interface'
import { createPdfConversionJob, streamPdfConversionJob, formatPdfConversionMessage } from 'services/pdf-conversion'
import SuccessBox from './components/SuccessBox'
import ImmediateSuccessBox from './components/ImmediateSuccessBox'
import { showUpgradeCardModal, UPGRADE_PDF_TO_WEBSITE_PAYLOAD } from 'actions'
import { ErrorBox } from '../ErrorBox/ErrorBox'
import { getSimulationDuration } from './components/LoadingBox'
import { uploadedMoreThanOneProject, uploadedFileTooBig } from 'utils/sendy'
import * as Sentry from '@sentry/react'
import { useHistory, useLocation } from 'react-router-dom'
import DEEP_LINK from 'utils/deepLinks'
import TINY_PLAN from 'constants/plans/tiny'
import SOLO_PLAN from 'constants/plans/solo'
import CustomModal from 'components/CustomModal/CustomModal'
import { calculateTotalSize, MAX_DIRECT_UPLOAD_SIZE } from 'services/create-update-site/pro'
import UpdateSiteModal from 'components/UpdateSiteModal/UpdateSiteModal'
import { isVideoFile } from 'helpers/file-type'
import { classifierScan } from 'services/site'
import { setLiveSitesStore } from 'Manage/actions'
import { checkVideoFilesAndSetPreview } from 'utils/videoFileUtils'
import useCheckScreenSize from '../../hooks/checkMobileScreen'
import { useUploadStats } from '../../hooks/useUploadStats'
import UploadStats from '../UploadStats/UploadStats'
import { F_PDF_TO_WEBSITE } from 'constants/plans/constants'
import { zipFolderFilesWithWorker } from 'utils/folder-upload'
import toast from 'react-hot-toast'
import './CreateUpdateSiteModal.css'

const MIN_SIZE_FOR_STATS = 50 * 1024 * 1024

const mergeSiteIntoList = (list, siteToMerge) => {
  if (!siteToMerge?.subdomain) return Array.isArray(list) ? list : []

  const safeList = Array.isArray(list) ? list : []
  const existingIndex = safeList.findIndex((site) => site?.subdomain === siteToMerge.subdomain)
  const existingSite = existingIndex !== -1 ? safeList[existingIndex] : undefined

  const normalizedStatus =
    existingSite?.linkStatus &&
    existingSite.linkStatus !== 'processing' &&
    (!siteToMerge.linkStatus || siteToMerge.linkStatus === 'processing')
      ? existingSite.linkStatus
      : siteToMerge.linkStatus || existingSite?.linkStatus || 'processing'

  const payload =
    existingIndex !== -1
      ? { ...existingSite, ...siteToMerge, linkStatus: normalizedStatus }
      : { ...siteToMerge, linkStatus: normalizedStatus }

  if (existingIndex === -1) {
    return [payload, ...safeList]
  }

  const nextList = [...safeList]
  nextList[existingIndex] = payload
  return nextList
}

const CreateUpdateSiteModal = ({
  sites,
  disableProFeatures,
  liveSitesCount,
  executeRecaptcha,
  onReplace,
  onShowCodeEditor,
  onOpenTextEditor,
  onOpenUrl,
  onOpenGithubModal,
  onOpenTransferRequestLinkModal,
  onOpenDropBox,
  viewRepos
}) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const { isMobile } = useCheckScreenSize()

  const [isOnline, setIsOnline] = useState(false)
  const [resetTrigger, setResetTrigger] = useState(0)
  const loadingSites = useSelector((state) => state.manage.loadingSitesStore)

  /** V1 */
  const { action, userProfile, show, data, customDomains, draggedFile, siteType, activeSpace, liveSitesStore } =
    useSelector(
      ({ manage: { createSiteModal, userProfile, customDomains, draggedFile, siteType, activeSpace, liveSites } }) => {
        return {
          action: createSiteModal.action || ACTION_CREATE,
          userProfile: userProfile,
          show: createSiteModal.visible,
          data: createSiteModal?.data || undefined,
          customDomains: customDomains.filter((c) => ['CONNECTED', 'VALIDATED'].includes(c.status?.toUpperCase())),
          activeSpace: activeSpace,
          draggedFile: draggedFile,
          siteType: siteType,
          liveSitesStore: liveSites
        }
      }
    )

  const liveSitesRef = useRef([])
  const activeSpaceRef = useRef(activeSpace)

  useEffect(() => {
    liveSitesRef.current = Array.isArray(liveSitesStore) ? liveSitesStore : []
  }, [liveSitesStore])

  useEffect(() => {
    activeSpaceRef.current = activeSpace
  }, [activeSpace])

  useEffect(() => {
    if (show) {
      const pathname = action === ACTION_CREATE ? '/manage/upload' : action === ACTION_UPDATE ? '/manage/update' : null

      if (pathname && location.pathname !== pathname) {
        history.push({ pathname, search: location.search })
      }
    }
  }, [show, resetTrigger])

  const modalValues = {
    action: action || ACTION_CREATE,
    data
  }

  /** Modal values V2 */
  const [modalState, setModalState] = useState({
    pageState: STATE_READY,
    fullDomain: '',
    site: undefined,
    files: [],
    newSite: undefined,
    currentFormValues: undefined,
    progress: 0,
    simulatedDurationProgress: 300
  })
  const { pageState, fullDomain, site, files, newSite, currentFormValues, progress, simulatedDurationProgress } =
    modalState
  const pageStateRef = useRef(STATE_READY)
  const [errorMessage, setErrorMessage] = useState('')
  const [multipartUpload, setMultipartUpload] = useState(false)
  const { uploadStats, uploadProgress: updateStatsProgress, resetStats } = useUploadStats()
  const [showUploadStats, setShowUploadStats] = useState(false)
  const [pdfConversionProgress, setPdfConversionProgress] = useState(0)
  const [pdfConversionMessage, setPdfConversionMessage] = useState('Starting conversion...')
  const [pdfJobId, setPdfJobId] = useState(null)

  const tlds = getTLDs({ account: userProfile })

  const shouldPreventModalValuesForFreeUser = () => {
    const isFreeUser = !userProfile.productId
    return isFreeUser && pageState === STATE_LOADING
  }

  const setPageState = (value) =>
    setModalState((prev) => ({ ...prev, pageState: typeof value === 'function' ? value(prev.pageState) : value }))
  const setNewSite = (value) =>
    setModalState((prev) => ({ ...prev, newSite: typeof value === 'function' ? value(prev.newSite) : value }))
  const setCurrentFormValues = (value) =>
    setModalState((prev) => ({
      ...prev,
      currentFormValues: typeof value === 'function' ? value(prev.currentFormValues) : value
    }))
  const setProgress = (value) =>
    setModalState((prev) => ({ ...prev, progress: typeof value === 'function' ? value(prev.progress) : value }))
  const setSimulatedDurationProgress = (value) =>
    setModalState((prev) => ({
      ...prev,
      simulatedDurationProgress: typeof value === 'function' ? value(prev.simulatedDurationProgress) : value
    }))

  const setModalValues = (mySite = undefined, siteFiles = [], myNewSite = undefined, state = STATE_READY) => {
    if (myNewSite) {
      dispatch(setAction(ACTION_UPDATE))
    }

    if (state === STATE_PREVIEW) {
      uploadedFileTooBig()
      dispatch(setPreviewModal(true))
      dispatch(setPreviewData(myNewSite))
      onClose()
    }

    const currentPageState = pageStateRef.current
    const shouldResetProgress = ![STATE_SUCCESS, STATE_REQUESTED].includes(state) && currentPageState !== STATE_LOADING

    setModalState((prev) => ({
      ...prev,
      site: myNewSite || mySite,
      newSite: state === STATE_SUCCESS ? myNewSite || mySite : state === STATE_PREVIEW ? prev.newSite : myNewSite,
      files: siteFiles,
      fullDomain: myNewSite?.subdomain || mySite?.subdomain || '',
      currentFormValues: undefined,
      pageState: state,
      // Preserve the previous progress so the loading bar doesn't jump backwards
      progress: shouldResetProgress ? 0 : Math.min(Math.max(prev.progress, 0), 100),
      simulatedDurationProgress: shouldResetProgress ? 300 : prev.simulatedDurationProgress || 300
    }))
  }

  useEffect(() => {
    pageStateRef.current = pageState
  }, [pageState])

  useEffect(() => {
    if (shouldPreventModalValuesForFreeUser()) {
      return
    }

    if (pageState !== STATE_SUCCESS && pageState !== STATE_REQUESTED && !isOnline) {
      const mySite = site || data
      setModalValues(mySite, undefined, undefined, STATE_READY)
    }
  }, [data, action, show])

  useEffect(() => {
    if (shouldPreventModalValuesForFreeUser()) {
      return
    }

    if (pageState === STATE_SUCCESS || pageState === STATE_PDF_CONVERTING) {
      return
    }

    setPageState(STATE_LOADING)
    const fetchSite = async () => {
      try {
        const { link, siteFiles } = await getSite(action, fullDomain)

        if (!link) {
          setModalValues(undefined, siteFiles)
          setPageState(STATE_READY)
          return
        }

        if (link?.linkStatus !== 'error') {
          setModalValues(link, siteFiles)
        } else {
          setPageState(STATE_ERROR)
        }
      } catch (error) {
        setPageState(STATE_ERROR)
        return
      }
      setPageState(STATE_READY)
    }

    if (action === ACTION_CREATE && pageState !== STATE_ERROR) {
      setModalValues()
    } else {
      if (pageState !== STATE_REQUESTED) fetchSite()
    }
  }, [fullDomain])

  useEffect(() => {
    if (shouldPreventModalValuesForFreeUser()) {
      return
    }

    const fetchSite = async () => {
      try {
        const { link, siteFiles } = await getSite(action, fullDomain)

        if (!link) {
          setIsOnline(false)
          return
        }

        if (link?.linkStatus === 'online') {
          setIsOnline(true)
        } else {
          setIsOnline(false)
        }
        if (link?.linkStatus !== 'error') {
          const currentPath = window.location.pathname
          if (currentPath !== DEEP_LINK.UPDATE_SITE) setModalValues(link, siteFiles, undefined, STATE_SUCCESS)
        } else {
          setPageState(STATE_ERROR)
        }
      } catch (error) {
        setPageState(STATE_ERROR)
        return
      }
    }

    if (action === ACTION_CREATE && pageState !== STATE_ERROR) {
      setModalValues(site, undefined, undefined, pageState)
    } else {
      if (fullDomain && pageState !== STATE_REQUESTED && pageState !== STATE_PREVIEW) fetchSite()
    }
  }, [loadingSites])

  useEffect(() => {
    if (!isOnline || !newSite?.subdomain) {
      return
    }

    const storeSite = Array.isArray(liveSitesStore)
      ? liveSitesStore.find((storedSite) => storedSite?.subdomain === newSite.subdomain)
      : undefined

    if (storeSite?.linkStatus === 'online') {
      if (newSite?.linkStatus !== 'online') {
        setNewSite((prev) => (prev ? { ...prev, linkStatus: 'online' } : prev))
      }
      return
    }

    const updatedSite = newSite?.linkStatus === 'online' ? newSite : { ...newSite, linkStatus: 'online' }

    if (newSite?.linkStatus !== 'online') {
      setNewSite(updatedSite)
    }

    const baseLiveSites = liveSitesRef.current.length ? liveSitesRef.current : Array.isArray(sites) ? sites : []
    const nextLiveSites = mergeSiteIntoList(baseLiveSites, updatedSite)
    dispatch(setLiveSitesStore(nextLiveSites))
  }, [isOnline, newSite?.subdomain, newSite?.linkStatus, liveSitesStore, sites, dispatch])

  useEffect(() => {
    const normalizedStatus = newSite?.linkStatus?.toLowerCase() || ''
    const shouldPoll = pageState === STATE_SUCCESS && newSite?.subdomain && normalizedStatus === 'processing'

    if (!shouldPoll) {
      return undefined
    }

    let cancelled = false
    let pollIntervalId

    const pollSiteStatus = async () => {
      if (cancelled) return

      try {
        const { link, siteFiles } = await getSite(action, newSite.subdomain)

        if (!link || cancelled) {
          return
        }

        setModalState((prev) => ({ ...prev, site: link, files: siteFiles || [], newSite: link }))

        const nextStatus = link?.linkStatus?.toLowerCase()
        if (nextStatus === 'online') {
          setIsOnline(true)
        } else if (nextStatus === 'error') {
          setIsOnline(false)
        }

        if (nextStatus && ['online', 'error'].includes(nextStatus)) {
          const baseLiveSitesForStatus = liveSitesRef.current.length
            ? liveSitesRef.current
            : Array.isArray(sites)
              ? sites
              : []
          const nextLiveSitesForStatus = mergeSiteIntoList(baseLiveSitesForStatus, link)
          dispatch(setLiveSitesStore(nextLiveSitesForStatus))
        }

        if (nextStatus && ['online', 'error'].includes(nextStatus)) {
          if (pollIntervalId) {
            clearInterval(pollIntervalId)
          }
          cancelled = true
        }
      } catch (error) {
        console.error('Failed to poll site status', error)
      }
    }

    pollSiteStatus()
    pollIntervalId = setInterval(pollSiteStatus, 5000)

    return () => {
      cancelled = true
      if (pollIntervalId) {
        clearInterval(pollIntervalId)
      }
    }
  }, [action, newSite?.linkStatus, newSite?.subdomain, pageState])

  const onClose = () => {
    setModalValues()
    dispatch(showCreateSiteModal(false))
    dispatch(toggleModal(false))
    dispatch(setDraggedFile(null))
    dispatch(setSiteType(null))
    setIsOnline(false)
    setMultipartUpload(false)
    setPdfConversionProgress(0)
    setPdfConversionMessage('Starting conversion...')
    setPdfJobId(null)
  }

  const onReset = (mySite = undefined, myFiles = []) => {
    setModalValues(mySite, myFiles)
    if (mySite) {
      dispatch(setAction(ACTION_UPDATE))
      setResetTrigger((prev) => prev + 1)
    }
    dispatch(setDraggedFile(null))
    dispatch(setSiteType(null))
    setIsOnline(false)
    setPdfConversionProgress(0)
    setPdfConversionMessage('Starting conversion...')
    setPdfJobId(null)
  }

  const onRetry = () => {
    setPageState(STATE_READY)
    setPdfConversionProgress(0)
    setPdfConversionMessage('Starting conversion...')
    setPdfJobId(null)
  }

  const handleFeatureModalComplete = () => {
    const site = liveSitesRef.current?.find((s) => s?.subdomain === newSite?.subdomain)
    if (site?.inPreviewMode) {
      dispatch(setPreviewModal(true))
      dispatch(setPreviewData(site))
      onClose()
    } else {
      setPageState(STATE_SUCCESS)
    }
  }

  const onConvertToWebsite = async (domainValues, uploadValues) => {
    if (!hasFeature(userProfile?.productId, F_PDF_TO_WEBSITE)) {
      dispatch(
        showUpgradeCardModal({
          ...UPGRADE_PDF_TO_WEBSITE_PAYLOAD,
          onClick: () => onUpgradePlan({ planId: SOLO_PLAN.id }, dispatch)
        })
      )
      return
    }

    try {
      setPageState(STATE_PDF_CONVERTING)
      setPdfConversionProgress(0)
      setPdfConversionMessage('Starting conversion...')

      const token = localStorage.getItem('token')
      const domain = domainValues?.subdomain || null

      // Create the PDF conversion job
      const jobData = await createPdfConversionJob(uploadValues.selectedFile, token, domain)
      const jobId = jobData.job_id
      setPdfJobId(jobId)

      // Stream the job progress
      const cleanup = streamPdfConversionJob(
        jobId,
        (data) => {
          // Handle progress updates
          if (data.progress) {
            setPdfConversionProgress(data.progress)
          }
          if (data.message) {
            setPdfConversionMessage(formatPdfConversionMessage(data.message))
          }

          // Handle completion
          if (data.type === 'published' && data.tiiny_url) {
            // PDF conversion successful, navigate to success
            let tiinyUrlHostname
            try {
              tiinyUrlHostname = new URL(data.tiiny_url).hostname
            } catch (e) {
              tiinyUrlHostname = data.tiiny_url.split('//')[1]?.split('/')[0]
            }

            const convertedSite = {
              subdomain: domain || tiinyUrlHostname,
              linkStatus: 'online',
              tiinyUrl: data.tiiny_url
            }
            setModalValues(convertedSite, [], convertedSite, STATE_SUCCESS)
            cleanup()
          } else if (data.type === 'error') {
            // Handle error
            setErrorMessage(data.error || data.message || 'Conversion failed')
            setPageState(STATE_ERROR)
            cleanup()
          }
        },
        (error) => {
          // Handle stream error
          setErrorMessage(error.message || 'Connection lost')
          setPageState(STATE_ERROR)
        }
      )

      // Store cleanup function for later use
      return () => cleanup()
    } catch (error) {
      console.error('PDF conversion error:', error)
      setErrorMessage(error?.response?.data?.error || error.message || 'Failed to start conversion')
      setPageState(STATE_ERROR)
    }
  }

  const isExceedAccountLimit = (formValues) => {
    const selectedFile = formValues?.uploadValues?.selectedFile || null
    const addedFiles = formValues?.uploadValues?.addedFiles || []
    const limitCheck = checkAccountSizeLimit({
      userProfile,
      sites,
      action,
      currentSite: site,
      selectedFile,
      addedFiles
    })
    if (limitCheck.exceedsLimit) {
      dispatch(
        showUpgradeCardModal({
          title: 'Storage limit exceeded',
          desc: `Please increase the storage limit on your account to upload this file.`,
          cta: 'Contact support',
          onClick: () => {
            window.$crisp.push(['do', 'chat:open'])
            onClose()
          }
        })
      )
      return true
    }
    return false
  }

  const onSubmit = async (formValues, initValues) => {
    if (formValues?.uploadValues?.selectedFile?.isRawFolder) {
      try {
        formValues.uploadValues.selectedFile = await zipFolderFilesWithWorker(formValues.uploadValues.selectedFile)
      } catch (error) {
        toast.error('Error compressing folder. Please try again.')
        return
      }
    }

    if (formValues?.uploadValues?.draggedFile?.isRawFolder && !formValues.uploadValues.selectedFile) {
      try {
        formValues.uploadValues.draggedFile = await zipFolderFilesWithWorker(formValues.uploadValues.draggedFile)
      } catch (error) {
        toast.error('Error compressing folder. Please try again.')
        return
      }
    }

    let myNewSite
    let myNewFiles

    history.push({ pathname: '/manage' })

    const containsPhpFile =
      isWasmerFile(formValues?.uploadValues?.selectedFile) ||
      formValues?.uploadValues?.fileType === 'PHP' ||
      formValues?.uploadValues?.isZipPhpFile

    if (!userProfile?.productId && containsPhpFile) {
      dispatch(
        showUpgradeCardModal({
          title: 'This upload type cannot be hosted on the free plan',
          desc: 'Please upgrade to upload PHP, Python, Node.js, or React ZIPs.',
          cta: 'Upgrade now',
          onClick: () => onUpgradePlan({ planId: TINY_PLAN.id }, dispatch)
        })
      )
      return
    }

    // check if account size limit is exceeded
    if (isExceedAccountLimit(formValues)) {
      return
    }

    if (action === ACTION_UPDATE && !canSubmitUpload(userProfile)) {
      dispatch(
        showUpgradeCardModal({
          title: "You've reached your daily upload limit",
          desc: `You can only update your project 3 times per day, please upgrade or return tomorrow.`,
          cta: 'Upgrade now',
          onClick: () => onUpgradePlan({ planId: TINY_PLAN.id }, dispatch)
        })
      )
      onClose()
      return
    }

    /** only on update */
    const domainValuesToUpdate = getUpdatedFields(formValues.domainValues, initValues.domainValues)
    const toggleValuesToUpdate = getUpdatedFields(formValues.toggleValues, initValues.toggleValues, true)
    const uploadValuesToUpdate = getUpdatedFields(formValues.uploadValues, initValues.uploadValues)

    const uploadProgress = ({ loaded, total }, { size }, isMultipartUpload = false) => {
      if (isMultipartUpload) setMultipartUpload(true)

      const currentProgress = isMultipartUpload
        ? Math.round((100 * loaded) / total)
        : Math.round(((100 * loaded) / total) * 0.5)

      if ((!isMultipartUpload || loaded) && currentProgress > progress) {
        setProgress(currentProgress)
      }

      if (currentProgress === 50 && !isMultipartUpload) {
        setSimulatedDurationProgress(getSimulationDuration(size))
      }

      if (size >= MIN_SIZE_FOR_STATS) {
        updateStatsProgress({ loaded, total }, { size }, isMultipartUpload)
      }
    }

    const executeRecaptchaWrapper = async () => {
      try {
        return await executeRecaptcha()
      } catch (error) {
        console.error(error)
        Sentry.captureException(error, {
          tags: {
            section: 'create-update-site-recaptcha-error'
          }
        })
        return null
      }
    }

    try {
      setPageState(STATE_LOADING)
      if (action === ACTION_UPDATE) {
        const currentFiles = formValues.uploadValues.selectedFile
          ? [formValues.uploadValues.selectedFile, ...(formValues.uploadValues.addedFiles || [])]
          : []
        const totalSize = await calculateTotalSize(currentFiles)
        const isVideoFiles = currentFiles.some((file) => isVideoFile(file.name, file.type))
        const isPresignedUpload = totalSize > MAX_DIRECT_UPLOAD_SIZE || isVideoFiles

        if (totalSize >= MIN_SIZE_FOR_STATS) {
          setShowUploadStats(true)
          resetStats()
        } else {
          setShowUploadStats(false)
        }

        if (domainValuesToUpdate || toggleValuesToUpdate || uploadValuesToUpdate) {
          const data = await updateSite(
            getFullDomainValue(initValues.domainValues.subdomain, initValues.domainValues.domainSuffix),
            updateSiteRequest({ domainValuesToUpdate, toggleValuesToUpdate, uploadValuesToUpdate, siteType }),
            executeRecaptchaWrapper,
            uploadProgress,
            isPresignedUpload,
            dispatch
          )

          myNewSite = data.link
          myNewFiles = data.files
          myNewSite = checkVideoFilesAndSetPreview(myNewSite, myNewFiles, userProfile)
        }
      }

      if (action === ACTION_CREATE) {
        const currentFiles = formValues.uploadValues.selectedFile
          ? [formValues.uploadValues.selectedFile, ...(formValues.uploadValues.addedFiles || [])]
          : []
        const totalSize = await calculateTotalSize(currentFiles)

        if (totalSize >= MIN_SIZE_FOR_STATS) {
          setShowUploadStats(true)
          resetStats()
        } else {
          setShowUploadStats(false)
        }

        const currentActiveSpace = activeSpaceRef.current
        const spaceId = currentActiveSpace && currentActiveSpace !== 'archive' ? currentActiveSpace : null
        const data = await createNewSite(
          createNewSiteRequest({
            domainValues: formValues.domainValues,
            toggleValues: formValues.toggleValues,
            uploadValues: getUpdatedFields(formValues.uploadValues, initValues.uploadValues),
            siteType,
            spaceId
          }),
          executeRecaptchaWrapper,
          uploadProgress,
          dispatch
        )

        if (data?.classifierScanFiles && data?.classifierScanFiles.length > 0) {
          ;(async () => {
            try {
              const { data: classifierScanData } = await classifierScan({
                classifierScanFiles: data.classifierScanFiles,
                domain: data.link.subdomain,
                email: userProfile?.email
              })

              const baseClassifierList = liveSitesRef.current.length
                ? liveSitesRef.current
                : Array.isArray(sites)
                  ? sites
                  : []
              const updatedClassifierSite = mergeSiteIntoList(baseClassifierList, classifierScanData.site)
              dispatch(setLiveSitesStore(updatedClassifierSite))
            } catch (error) {
              console.error(error)
            }
          })()
        }

        myNewSite = data.link
        myNewFiles = data.files || []
        myNewSite = checkVideoFilesAndSetPreview(myNewSite, myNewFiles, userProfile)
      }

      const isFreeUser = !userProfile?.productId

      if (myNewSite?.subdomain) {
        const baseLiveSites = liveSitesRef.current.length ? liveSitesRef.current : Array.isArray(sites) ? sites : []
        const nextLiveSites = mergeSiteIntoList(baseLiveSites, myNewSite)
        dispatch(setLiveSitesStore(nextLiveSites))
      }

      if (isFreeUser && !myNewSite?.inPreviewMode) {
        setModalState((prev) => ({
          ...prev,
          progress: 100,
          site: myNewSite || prev.site,
          files: myNewFiles || prev.files,
          newSite: myNewSite,
          fullDomain: myNewSite?.subdomain || prev.site?.subdomain || '',
          currentFormValues: undefined,
          pageState: STATE_LOADING
        }))
      } else {
        const targetState = myNewSite?.inPreviewMode ? STATE_PREVIEW : STATE_SUCCESS
        setPageState(STATE_REQUESTED)
        setModalValues(myNewSite || site, myNewFiles || files, myNewSite, targetState)
      }
      setShowUploadStats(false)
    } catch (error) {
      console.error(error)
      setCurrentFormValues(formValues)
      setShowUploadStats(false)

      const errorCode = error?.response?.data?.code || error?.code
      const errorMessage = getErrorMessage(error)
      if (errorCode === 'DOMAIN_EXISTS') {
        const linkName =
          (formValues?.domainValues?.subdomain || formValues?.subdomain) +
          (formValues?.domainValues?.domainSuffix || formValues?.domainSuffix || '.tiiny.io')
        const existingSite = sites?.find((site) => site.subdomain === linkName)

        if (existingSite) {
          localStorage.setItem('cancelPresignedUpload', 'true')
          window.cancelPresignedUpload = true
          localStorage.setItem('domainExistsSite', JSON.stringify(existingSite))
          dispatch(showCreateSiteModal(false))
          history.push('/manage/domain-exist')
          return
        }
      }

      if (error?.message === 'UPLOAD_CANCELLED' || error?.message === 'Upload response is null') {
        return
      }

      setPageState(STATE_ERROR)
      if (errorMessage && errorMessage !== '') {
        setErrorMessage(getErrorMessage(error))
      } else if (errorMessage && errorMessage === '') {
        setErrorMessage('Oops, something went wrong')
        console.error(error)
      } else {
        setErrorMessage('Oops, something went wrong')
        Sentry.captureException(error, {
          tags: {
            section: 'create-update-site-error'
          }
        })
      }
    } finally {
      dispatch(fetchUserData(false))
    }
  }

  const trialAccount = !userProfile.productId

  const formValues = {
    action,
    userProfile,
    site: site,
    files: files,
    customDomains,
    tlds,
    disableProFeatures,
    draggedFile,
    currentFormValues,
    siteType
  }

  const handleModalClose = () => {
    history.push({ pathname: '/manage' })
    if (pageState !== STATE_LOADING && pageState !== STATE_REQUESTED && pageState !== STATE_PDF_CONVERTING) {
      localStorage.removeItem('active_custom_domain')
      localStorage.removeItem('active_custom_subdomain')
      onClose()
    }
  }

  useEffect(() => {
    const handleBeforeUnload = async (event) => {
      if (
        show &&
        (pageState === STATE_LOADING || pageState === STATE_REQUESTED || pageState === STATE_PDF_CONVERTING)
      ) {
        localStorage.setItem('cancelPresignedUpload', 'true')
        event.preventDefault()
        event.returnValue = ''
        /** Chrome requires returnValue to be set */
        return ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [pageState, show])

  return (
    <CustomModal
      show={show}
      aria-labelledby="contained-modal-title-vcenter"
      centered
      onHide={handleModalClose}
      size={action === ACTION_UPDATE && pageState !== STATE_SUCCESS ? 'lg' : 'md'}
      {...modalValues}
      className={`${pageState == STATE_SUCCESS && trialAccount ? 'success_modal' : ''} ${(pageState === STATE_LOADING || pageState === STATE_REQUESTED || pageState === STATE_PDF_CONVERTING) && isTrialAccount(userProfile) ? 'feature-modal-container' : ''} ${(pageState === STATE_LOADING || pageState === STATE_REQUESTED || pageState === STATE_PDF_CONVERTING) && !isTrialAccount(userProfile) ? 'progress-modal-container' : ''}`}
      enforceFocus={false}
      mobileCloseButton={pageState == STATE_SUCCESS || action === ACTION_UPDATE ? false : true}
      mobileFixedCloseButton={true}
    >
      <div>
        {(() => {
          switch (pageState) {
            case STATE_ERROR:
              return <ErrorBox onClick={onRetry} message={errorMessage || 'Oops, something went wrong'} />
            case STATE_PDF_CONVERTING: {
              return (
                <LoadingBox
                  showProgressBar={true}
                  progress={pdfConversionProgress}
                  message={pdfConversionMessage}
                  action={action}
                  userProfile={userProfile}
                  onComplete={handleFeatureModalComplete}
                  dispatch={dispatch}
                  liveSites={liveSitesRef.current}
                  isPdfConversion={true}
                />
              )
            }
            case STATE_LOADING:
            case STATE_REQUESTED: {
              return (
                <>
                  <LoadingBox
                    showProgressBar={progress > 0 && (progress < 50 || multipartUpload)}
                    showSimulationProgressBar={progress >= 50 && progress < 100 && !multipartUpload}
                    progress={progress}
                    simulatedDurationProgress={simulatedDurationProgress}
                    action={action}
                    userProfile={userProfile}
                    onComplete={handleFeatureModalComplete}
                    dispatch={dispatch}
                    liveSites={liveSitesRef.current}
                  />
                  {showUploadStats && <UploadStats uploadStats={uploadStats} />}
                </>
              )
            }
            case STATE_SUCCESS: {
              const normalizedStatus = (newSite?.linkStatus || '').toLowerCase()
              const showImmediateSuccess = !multipartUpload && (!normalizedStatus || normalizedStatus === 'online')

              if (showImmediateSuccess) {
                return (
                  <ImmediateSuccessBox
                    userProfile={userProfile}
                    site={newSite}
                    files={files}
                    onClose={onClose}
                    onReset={onReset}
                  />
                )
              }

              return (
                <SuccessBox
                  userProfile={userProfile}
                  site={newSite}
                  files={files}
                  onClose={onClose}
                  onReset={onReset}
                  isOnline={isOnline}
                  multipartUpload={multipartUpload}
                />
              )
            }
            default:
              return (
                <div className={isMobile ? 'px-4 pt-4 pb-0 mobile-upload-modal' : 'p-4'}>
                  {action === ACTION_UPDATE ? (
                    <UpdateSiteModal
                      formValues={formValues}
                      onSubmit={onSubmit}
                      validate={validate}
                      onClose={handleModalClose}
                    />
                  ) : (
                    <CreateUpdateSiteForm
                      formValues={formValues}
                      onSubmit={onSubmit}
                      validate={validate}
                      onConvertToWebsite={onConvertToWebsite}
                      onShowCodeEditor={onShowCodeEditor}
                      onOpenTextEditor={onOpenTextEditor}
                      onOpenUrl={onOpenUrl}
                      onOpenGithubModal={onOpenGithubModal}
                      onOpenTransferRequestLinkModal={onOpenTransferRequestLinkModal}
                      onOpenDropBox={onOpenDropBox}
                      viewRepos={viewRepos}
                      isMobile={isMobile}
                    />
                  )}
                </div>
              )
          }
        })()}
      </div>
    </CustomModal>
  )
}

export default CreateUpdateSiteModal
